/**
 * Print body for the App workspace host.
 *
 * The descriptor lives in the print-session registry rather than in the path,
 * because a print form carries a filled — possibly hand-edited — model that
 * only exists in this browser session. A tab whose session is gone (someone
 * reloaded on `/print/<id>`, or pasted the link) says so instead of rendering
 * an empty sheet.
 *
 * Re-print reuses the same `/print/<id>` path (WorkspaceHost `key={tab.path}`
 * stays put). The session registry bumps a revision when the stored descriptor
 * is replaced; we remount PrintWindowView on that revision so the new model
 * paints instead of the first fill.
 */
import React, { useSyncExternalStore } from 'react'
import Box from '@mui/material/Box'
import { PrintWindowView } from '@/runtime/print/PrintWindowView'
import { WorkspaceEmptyGate } from './WorkspaceEmptyGate'
import {
  getPrintSession,
  getPrintSessionRevision,
  subscribePrintSessions,
} from '@/runtime/print/printSessions'

export interface PrintTabBodyProps {
  sessionId: string
  onTitleChange?: (title: string) => void
}

export const PrintTabBody: React.FC<PrintTabBodyProps> = ({ sessionId, onTitleChange }) => {
  const revision = useSyncExternalStore(
    subscribePrintSessions,
    () => getPrintSessionRevision(sessionId),
  )
  const descriptor = getPrintSession(sessionId)

  if (!descriptor) {
    return <WorkspaceEmptyGate reason="printSession" />
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <PrintWindowView
        key={revision}
        descriptor={descriptor}
        onTitleChange={onTitleChange}
      />
    </Box>
  )
}
